/** @header menu */
import React, { memo, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, Platform, Alert } from 'react-native';
import { layoutLtrStyle, useTranslation } from '../../shared/i18n';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  useAnimatedReaction,
  runOnJS,
  interpolate,
  interpolateColor,
  Extrapolation,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import type { SharedValue } from 'react-native-reanimated';
import { MenuFlowDateSlider } from './MenuFlowDateSlider';
import { MenuFlowFoodTypeBar } from './MenuFlowFoodTypeBar';
import { MenuFlowCounter } from './MenuFlowCounter';
import { MenuFlowSectionHeader } from './MenuFlowSectionHeader';
import { DailyMenuFlowDateSlider } from '../../dailyMenu/components/DailyMenuFlowDateSlider';
import { DailyMenuFlowFoodTypeBar } from '../../dailyMenu/components/DailyMenuFlowFoodTypeBar';
import { DailyMenuFlowCounter } from '../../dailyMenu/components/DailyMenuFlowCounter';
import { DailyMenuFlowSectionHeader } from '../../dailyMenu/components/DailyMenuFlowSectionHeader';
import type { DailyMenuSection } from '../../dailyMenu/components/dailyMenuSectionTypes';
import { Clickable } from '../../components/common/Clickable';
import { SvgIcon } from '../../components/common/SvgIcon';
import { ButtonLoader } from '../../components/load-main/ButtonLoader';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { centeredLabelText, androidStripElevationSeams, androidHeaderTopInset, MENU_FLOW_HEADER_DATE_BLOCK_HEIGHT } from '../../shared/theme/sharedStyles';
import { MenuFlowSection } from '../../data/domains/menu';

const block_first = MENU_FLOW_HEADER_DATE_BLOCK_HEIGHT;
const block_second = 38;
const block_third = 38;
const block_fourth = 38;
/** Collapsed dock stack (blocks 2–4 + inter-block gaps). Used by `useDailyMenuFlowController`. */
export const DAILY_MENU_DOCK_BLOCK_MIN_HEIGHT = block_second + 8 + block_third + 8 + block_fourth;

function createMenuFlowHeaderStyles(c: ThemeColors) {
  return StyleSheet.create({
    headerShell: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000000,
      overflow: 'hidden',
      flexDirection: 'column',
    },
    blurLayer: {
      zIndex: 0,
    },
    headerContentWrap: {
      flex: 1,
      minHeight: 0,
    },
    overlay: {
      ...StyleSheet.absoluteFillObject,
      zIndex: 1,
    },
    container: {
      width: '100%',
      zIndex: 1,
    },
    content: {
      width: '100%',
      paddingHorizontal: 12,
    },
    syncingRow: {
      position: 'absolute',
      right: 20,
      top: 60,
      zIndex: 1001,
    },
    staggerBlock: {
      borderRadius: 16,
      shadowColor: c.shadowColor,
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 1,
      elevation: 12,
      backfaceVisibility: 'hidden',
      overflow: 'hidden',
      ...androidStripElevationSeams(),
    },
    staggerBlockFirst: {
      backgroundColor: c.primary,
      paddingHorizontal: 8,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      marginBottom: 8,
    },
    staggerBlockSecond: {
      marginBottom: 8,
      height: block_second,
      padding: 4,
      borderRadius: 16,
    },
    staggerBlockThird: {
      height: block_third,
      paddingVertical: 4,
      paddingHorizontal: 4,
      borderRadius: 16,
      justifyContent: 'center',
    },
    staggerBlockFourth: {
      height: block_fourth,
      padding: 4,
      borderRadius: 16,
    },
    countersRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      flex: 1,
      minHeight: 0,
    },
    counterLeft: {
      flexShrink: 0,
    },
    counterRight: {
      flexShrink: 0,
    },
    boxSelectorInBar: {
      flexShrink: 1,
      minWidth: 0,
      maxWidth: 200,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 4,
    },
    boxSelectorInBarInner: {
      ...layoutLtrStyle,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      flexWrap: 'nowrap',
      gap: 4,
    },
    boxTabInBar: {
      paddingVertical: 2,
      paddingHorizontal: 10,
      borderRadius: 6,
      backgroundColor: c.primaryUltra,
      borderWidth: 1,
      borderColor: c.primaryFade,
      minWidth: 44,
      alignItems: 'center',
      justifyContent: 'center',
    },
    boxTabInBarActive: {
      backgroundColor: c.primary,
      borderColor: c.primary,
    },
    boxTabTextInBar: {
      ...centeredLabelText,
      fontSize: 11,
      fontWeight: '600',
      color: c.text,
    },
    boxTabTextInBarActive: {
      color: c.surface,
    },
    cancelBoxIconBtn: {
      width: 28,
      height: 28,
      minWidth: 28,
      minHeight: 28,
      marginStart: 12,
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.primaryUltra,
    },
    cancelBoxIconBtnInner: {
      width: 28,
      height: 28,
      alignItems: 'center',
      justifyContent: 'center',
    },
    doubleBoxCtaInBar: {
      paddingVertical: 2,
      paddingHorizontal: 6,
      borderRadius: 6,
      backgroundColor: c.primaryUltra,
      borderWidth: 1,
      borderColor: c.primaryFade,
      alignItems: 'center',
      justifyContent: 'center',
    },
    doubleBoxCtaTextInBar: {
      ...centeredLabelText,
      fontSize: 11,
      fontWeight: '600',
      color: c.primary,
    },
  });
}

type MenuFlowHeaderStyles = ReturnType<typeof createMenuFlowHeaderStyles>;

export interface MenuFlowHeaderProps {
  menuMode?: 'classic' | 'daily';
  paddingTop?: number;
  /** Offset header below a stack `ScreenHeader` (guest browse). */
  topOffset?: number;
  scrollY: SharedValue<number>;
  scrollRange: number;
  fullHeaderHeight: number;
  collapsedHeaderHeight: number;
  /** When > 0, header reserves this height when collapsed so sticky section row stays visible */
  stickySectionHeight?: number;
  dateSliderDates: string[];
  selectableDates: string[];
  selectedDate: string;
  onDateSelect: (date: string) => void;
  dayConfigMap: { [date: string]: any };
  storeDayConfig: any;
  foodTypes: string[];
  /** Optional map from food type value to localized display label (for API type_name / a_type_name). */
  foodTypeLabels?: Record<string, string>;
  selectedFoodType: string;
  onSelectFoodType: (type: string) => void;
  mealsCount: number;
  snacksCount: number;
  selectedMealsCount: number;
  selectedSnacksCount: number;
  isMealActive?: boolean;
  isSnackActive?: boolean;
  isSyncing?: boolean;
  /** Shared value for date slider collapsed. Header syncs to local state for pointer events. */
  lastDateSliderCollapsedSV: SharedValue<boolean>;
  reduceMotion?: boolean;
  sections: MenuFlowSection[] | DailyMenuSection[];
  /** Shared value for section index (from scroll worklet). Header syncs this to local state for section title. */
  currentSectionIndexSV: SharedValue<number>;
  /** Double box: show Box 1 / Box 2 selector when true */
  isDoubleBoxForDate?: boolean;
  activeBox?: 'box1' | 'box2';
  onBoxSelect?: (box: 'box1' | 'box2') => void;
  /** Dates to disable in the date slider (e.g. locked next day when double box active) */
  forcedDisabledDates?: string[];
  /** When double box is active: cancel double box (removes box 2). */
  onCancelBox?: () => void;
  /** When double box is not active for this date: call to enable second box for the day (shows Box 1/Box 2 selector). */
  onRequestDoubleBox?: () => void;
}

const SyncingIndicator = memo(({ styles, primaryColor }: { styles: MenuFlowHeaderStyles; primaryColor: string }) => (
  <View style={styles.syncingRow}>
    <ButtonLoader size={18} color={primaryColor} />
  </View>
));

const CountersRow = memo(
  ({
    menuMode = 'classic',
    mealsCount,
    snacksCount,
    selectedMealsCount,
    selectedSnacksCount,
    isMealActive,
    isSnackActive,
    isDoubleBoxForDate,
    activeBox,
    onBoxSelect,
    onCancelBox,
    onRequestDoubleBox,
    styles,
    textSecondary,
  }: {
    menuMode?: 'classic' | 'daily';
    mealsCount: number;
    snacksCount: number;
    selectedMealsCount: number;
    selectedSnacksCount: number;
    isMealActive: boolean;
    isSnackActive: boolean;
    isDoubleBoxForDate?: boolean;
    activeBox?: 'box1' | 'box2';
    onBoxSelect?: (box: 'box1' | 'box2') => void;
    onCancelBox?: () => void;
    onRequestDoubleBox?: () => void;
    styles: MenuFlowHeaderStyles;
    textSecondary: string;
  }) => {
    const { t } = useTranslation();
    const Counter = menuMode === 'daily' ? DailyMenuFlowCounter : MenuFlowCounter;
    const show_box_selector = isDoubleBoxForDate && onBoxSelect;
    const show_add_second = !isDoubleBoxForDate && onRequestDoubleBox;
    return (
      <View style={styles.countersRow}>
        <View style={styles.counterLeft}>
          <Counter label={t('subscriptions.meals')} count={mealsCount} selectedCount={selectedMealsCount} isActive={isMealActive} />
        </View>
        <View style={styles.boxSelectorInBar} pointerEvents="box-none">
          {show_box_selector && (
            <View style={styles.boxSelectorInBarInner} pointerEvents="box-none">
              <Clickable
                onPress={() => onBoxSelect!('box1')}
                style={[styles.boxTabInBar, activeBox === 'box1' && styles.boxTabInBarActive]}
                accessibilityLabel={t('menu.box1')}
                accessibilityRole="button"
                hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}>
                <Text style={[styles.boxTabTextInBar, activeBox === 'box1' && styles.boxTabTextInBarActive]} numberOfLines={1}>
                  {t('menu.box1')}
                </Text>
              </Clickable>
              <Clickable
                onPress={() => onBoxSelect!('box2')}
                style={[styles.boxTabInBar, activeBox === 'box2' && styles.boxTabInBarActive]}
                accessibilityLabel={t('menu.box2')}
                accessibilityRole="button"
                hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}>
                <Text style={[styles.boxTabTextInBar, activeBox === 'box2' && styles.boxTabTextInBarActive]} numberOfLines={1}>
                  {t('menu.box2')}
                </Text>
              </Clickable>
              {onCancelBox && (
                <Clickable
                  onPress={() => {
                    Alert.alert(
                      t('menu.cancelBoxTitle'),
                      t('menu.cancelBoxMessage'),
                      [
                        { text: t('menu.cancelBoxConfirm'), onPress: () => onCancelBox() },
                        { text: t('common.cancel'), style: 'cancel' },
                      ],
                    );
                  }}
                  style={styles.cancelBoxIconBtn}
                  accessibilityLabel={t('menu.cancelBoxTitle')}
                  accessibilityRole="button">
                  <View style={styles.cancelBoxIconBtnInner}>
                    <SvgIcon name="x" size={14} color={textSecondary} />
                  </View>
                </Clickable>
              )}
            </View>
          )}
          {show_add_second && onRequestDoubleBox && (
            <Clickable
              onPress={() => {
                Alert.alert(t('menu.enableDoubleBoxTitle'), t('menu.enableDoubleBoxMessage'), [
                  {
                    text: t('menu.enableDoubleBoxConfirm'),
                    onPress: () => {
                      onRequestDoubleBox();
                    },
                  },
                  { text: t('common.cancel'), style: 'cancel' },
                ]);
              }}
              style={styles.doubleBoxCtaInBar}
              accessibilityLabel={t('menu.addSecondBoxHint')}
              accessibilityRole="button">
              <Text style={styles.doubleBoxCtaTextInBar} numberOfLines={1}>
                {t('menu.doubleBox')}
              </Text>
            </Clickable>
          )}
        </View>
        <View style={styles.counterRight}>
          <Counter label={t('subscriptions.snacks')} count={snacksCount} selectedCount={selectedSnacksCount} isActive={isSnackActive} />
        </View>
      </View>
    );
  },
);

const MenuFlowHeaderComponent = ({
  menuMode = 'classic',
  topOffset = 0,
  scrollY,
  scrollRange,
  fullHeaderHeight,
  collapsedHeaderHeight,
  dateSliderDates,
  selectableDates,
  selectedDate,
  onDateSelect,
  dayConfigMap,
  storeDayConfig: _storeDayConfig,
  foodTypes,
  foodTypeLabels,
  selectedFoodType,
  onSelectFoodType,
  mealsCount,
  snacksCount,
  selectedMealsCount,
  selectedSnacksCount,
  isMealActive = false,
  isSnackActive = false,
  isSyncing = false,
  lastDateSliderCollapsedSV,
  reduceMotion = false,
  sections = [],
  currentSectionIndexSV,
  isDoubleBoxForDate = false,
  activeBox = 'box1',
  onBoxSelect,
  forcedDisabledDates = [],
  onCancelBox,
  onRequestDoubleBox,
}: MenuFlowHeaderProps) => {
  const is_daily = menuMode === 'daily';
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const themeColors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createMenuFlowHeaderStyles(themeColors), [themeColors]);
  const [localSectionIndex, setLocalSectionIndex] = useState(0);
  const [isDateSliderCollapsedLocal, setIsDateSliderCollapsedLocal] = useState(false);

  useAnimatedReaction(
    () => currentSectionIndexSV.value,
    idx => {
      runOnJS(setLocalSectionIndex)(Math.max(0, Math.floor(idx)));
    },
    [currentSectionIndexSV],
  );

  useAnimatedReaction(
    () => lastDateSliderCollapsedSV.value,
    collapsed => {
      runOnJS(setIsDateSliderCollapsedLocal)(collapsed);
    },
    [lastDateSliderCollapsedSV],
  );

  const headSoffset = useMemo(
    () => collapsedHeaderHeight + androidHeaderTopInset(insets.top),
    [collapsedHeaderHeight, insets.top],
  );
  const safe_top_inset = androidHeaderTopInset(insets.top);

  const current_section = useMemo(
    () => sections[localSectionIndex] ?? sections[0] ?? null,
    [sections, localSectionIndex],
  );

  const is_limit_reached_boolean = Boolean(
    current_section &&
      current_section.displayLimit > 0 &&
      current_section.selectedQuantity >= current_section.displayLimit,
  );
  const progress_limit_reached = useSharedValue(is_limit_reached_boolean ? 1 : 0);

  useEffect(() => {
    if (is_daily) {
      progress_limit_reached.value = withSpring(is_limit_reached_boolean ? 1 : 0, { stiffness: 500, damping: 50 });
    } else {
      progress_limit_reached.value = reduceMotion
        ? withTiming(is_limit_reached_boolean ? 1 : 0, { duration: 160 })
        : withSpring(is_limit_reached_boolean ? 1 : 0, { stiffness: 380, damping: 42 });
    }
  }, [is_limit_reached_boolean, progress_limit_reached, reduceMotion, is_daily]);

  const progress = useDerivedValue(() => interpolate(scrollY.value, [0, scrollRange], [0, 1], Extrapolation.CLAMP));

  const header_shell_transparent = is_daily ? `${themeColors.background}00` : `#00000000`;
  // Expanded: DATE_BLOCK + flex-end clears status bar (DateSlider must not also pad inset).
  // Collapsed: shell paddingTop carries the inset once the date band is gone.
  const headerShellStyle = useAnimatedStyle(() => {
    const collapsed_height = headSoffset;
    return {
      height: interpolate(progress.value, [0, 1], [fullHeaderHeight, collapsed_height], Extrapolation.CLAMP),
      backgroundColor: interpolateColor(progress.value, [0, 1], [header_shell_transparent, themeColors.primaryUltra], 'RGB'),
      paddingTop: interpolate(progress.value, [0, 1], [0, Math.max(0, safe_top_inset - 10)], Extrapolation.CLAMP),
    };
  }, [headSoffset, fullHeaderHeight, header_shell_transparent, themeColors.primaryUltra, safe_top_inset]);

  const dateSliderWrapStyle = useAnimatedStyle(() => {
    if (is_daily) {
      return {
        height: interpolate(progress.value, [0, 1], [block_first, 0], Extrapolation.CLAMP),
        opacity: interpolate(progress.value, [0, 0.75, 1], [1, 0, 0], Extrapolation.CLAMP),
        marginBottom: interpolate(progress.value, [0, 1], [8, 16], Extrapolation.CLAMP),
        transform: [
          { translateY: interpolate(progress.value, [0, 1], [0, -80], Extrapolation.CLAMP) },
          { perspective: 1000 },
          { rotateX: `${interpolate(progress.value, [0, 1], [0, -90], Extrapolation.CLAMP)}deg` },
        ],
        borderBottomLeftRadius: interpolate(progress.value, [0, 1], [26, 100], Extrapolation.CLAMP),
        borderBottomRightRadius: interpolate(progress.value, [0, 1], [26, 100], Extrapolation.CLAMP),
      };
    }
    return {
      height: interpolate(progress.value, [0, 1], [block_first, 0], Extrapolation.CLAMP),
      opacity: interpolate(progress.value, [0, 0.55, 1], [1, 0, 0], Extrapolation.CLAMP),
      marginBottom: interpolate(progress.value, [0, 1], [8, 12], Extrapolation.CLAMP),
    };
  });

  const countersWrapStyle = useAnimatedStyle(() => {
    const base = { marginBottom: interpolate(progress.value, [0, 1], [8, 4], Extrapolation.CLAMP) };
    if (is_daily) {
      return {
        ...base,
        transform: [{ scale: interpolate(progress.value, [0, 1], [1, 0.96], Extrapolation.CLAMP) }],
      };
    }
    return base;
  });

  const mealBarWrapStyle = useAnimatedStyle(() => {
    if (!is_daily) {
      return {};
    }
    return {
      transform: [{ scale: interpolate(progress.value, [0, 1], [1, 0.96], Extrapolation.CLAMP) }],
    };
  });

  const sectionHeaderWrapStyle = useAnimatedStyle(() => {
    const base = {
      backgroundColor: interpolateColor(progress_limit_reached.value, [0, 1], [themeColors.borderLight, themeColors.primary], 'RGB'),
      marginTop: interpolate(progress.value, [0, 1], [8, 4], Extrapolation.CLAMP),
    };
    if (is_daily) {
      return {
        ...base,
        transform: [{ scale: interpolate(progress.value, [0, 1], [1, 0.96], Extrapolation.CLAMP) }],
      };
    }
    return base;
  });

  const bgUpdate = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(progress.value, [0, 1], [themeColors.borderLight, themeColors.surface], 'RGB'),
  }));

  const is_box2_sv = useSharedValue(activeBox === 'box2' ? 1 : 0);
  useEffect(() => {
    is_box2_sv.value = activeBox === 'box2' ? 1 : 0;
  }, [activeBox, is_box2_sv]);

  const countersBlockBgStyle = useAnimatedStyle(() => {
    const base = interpolateColor(progress.value, [0, 1], [themeColors.borderLight, themeColors.surface], 'RGB');
    return {
      backgroundColor: is_box2_sv.value === 1 ? themeColors.tertiaryFade : base,
    };
  });

  const overlayBgStyle = useMemo(() => ({ backgroundColor: themeColors.primaryFade }), [themeColors.primaryFade]);

  const overlayStyle = useAnimatedStyle(() => ({
    opacity: interpolate(progress.value, [0, 1], [0, is_daily ? 1 : 0.45], Extrapolation.CLAMP),
  }));

  const dateSliderPointerEvents = isDateSliderCollapsedLocal ? 'none' : 'auto';

  return (
    <Animated.View style={[styles.headerShell, topOffset > 0 ? { top: topOffset } : null, headerShellStyle]} pointerEvents="box-none">
      <View
        style={[
          StyleSheet.absoluteFillObject,
          styles.blurLayer,
          { backgroundColor: themeColors.surface },
        ]}
      />
      <View
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: StyleSheet.hairlineWidth,
          backgroundColor: themeColors.borderSubtle,
        }}
      />
      <Animated.View pointerEvents="none" style={[styles.overlay, overlayBgStyle, overlayStyle]} />

      <View style={styles.headerContentWrap}>
        <View style={styles.container}>
          {isSyncing && <SyncingIndicator styles={styles} primaryColor={themeColors.primary} />}

          <View style={styles.content}>
            <Animated.View
              style={[styles.staggerBlock, styles.staggerBlockFirst, dateSliderWrapStyle]}
              pointerEvents={dateSliderPointerEvents}>
              {is_daily ? (
                <DailyMenuFlowDateSlider
                  availableDates={dateSliderDates}
                  selectableDates={selectableDates}
                  selectedDate={selectedDate}
                  onDateSelect={onDateSelect}
                  dayConfigMap={dayConfigMap}
                  forcedDisabledDates={forcedDisabledDates}
                />
              ) : (
                <MenuFlowDateSlider
                  availableDates={dateSliderDates}
                  selectableDates={selectableDates}
                  selectedDate={selectedDate}
                  onDateSelect={onDateSelect}
                  dayConfigMap={dayConfigMap}
                  forcedDisabledDates={forcedDisabledDates}
                />
              )}
            </Animated.View>

            <Animated.View
              style={[styles.staggerBlock, styles.staggerBlockSecond, countersBlockBgStyle, countersWrapStyle]}>
              <CountersRow
                menuMode={menuMode}
                mealsCount={mealsCount}
                snacksCount={snacksCount}
                selectedMealsCount={selectedMealsCount}
                selectedSnacksCount={selectedSnacksCount}
                isMealActive={isMealActive}
                isSnackActive={isSnackActive}
                isDoubleBoxForDate={isDoubleBoxForDate}
                activeBox={activeBox}
                onBoxSelect={onBoxSelect}
                onCancelBox={onCancelBox}
                onRequestDoubleBox={onRequestDoubleBox}
                styles={styles}
                textSecondary={themeColors.textSecondary}
              />
            </Animated.View>

            <Animated.View style={[styles.staggerBlock, styles.staggerBlockThird, bgUpdate, is_daily ? mealBarWrapStyle : null]}>
              {is_daily ? (
                <DailyMenuFlowFoodTypeBar foodTypes={foodTypes} foodTypeLabels={foodTypeLabels} selectedFoodType={selectedFoodType} onSelectFoodType={onSelectFoodType} />
              ) : (
                <MenuFlowFoodTypeBar foodTypes={foodTypes} foodTypeLabels={foodTypeLabels} selectedFoodType={selectedFoodType} onSelectFoodType={onSelectFoodType} />
              )}
            </Animated.View>

            <Animated.View style={[styles.staggerBlock, styles.staggerBlockFourth, sectionHeaderWrapStyle]}>
              {is_daily ? (
                <DailyMenuFlowSectionHeader currentSection={current_section} />
              ) : (
                <MenuFlowSectionHeader currentSection={current_section} />
              )}
            </Animated.View>
          </View>
        </View>
      </View>
    </Animated.View>
  );
};

function menuFlowHeaderPropsAreEqual(prev: MenuFlowHeaderProps, next: MenuFlowHeaderProps): boolean {
  if (
    prev.menuMode !== next.menuMode ||
    prev.scrollY !== next.scrollY ||
    prev.scrollRange !== next.scrollRange ||
    prev.fullHeaderHeight !== next.fullHeaderHeight ||
    prev.collapsedHeaderHeight !== next.collapsedHeaderHeight ||
    prev.selectedDate !== next.selectedDate ||
    prev.selectedFoodType !== next.selectedFoodType ||
    prev.mealsCount !== next.mealsCount ||
    prev.snacksCount !== next.snacksCount ||
    prev.selectedMealsCount !== next.selectedMealsCount ||
    prev.selectedSnacksCount !== next.selectedSnacksCount ||
    prev.isSyncing !== next.isSyncing ||
    prev.lastDateSliderCollapsedSV !== next.lastDateSliderCollapsedSV ||
    prev.currentSectionIndexSV !== next.currentSectionIndexSV
  ) {
    return false;
  }
  if (prev.foodTypes !== next.foodTypes || prev.foodTypeLabels !== next.foodTypeLabels || prev.dateSliderDates !== next.dateSliderDates || prev.selectableDates !== next.selectableDates || prev.dayConfigMap !== next.dayConfigMap) {
    return false;
  }
  if (prev.isDoubleBoxForDate !== next.isDoubleBoxForDate || prev.activeBox !== next.activeBox || prev.onCancelBox !== next.onCancelBox || prev.onRequestDoubleBox !== next.onRequestDoubleBox) {return false;}
  if ((prev.forcedDisabledDates ?? []).join('|') !== (next.forcedDisabledDates ?? []).join('|')) {
    return false;
  }
  return prev.sections === next.sections;
}

const MenuFlowHeader = memo(MenuFlowHeaderComponent, menuFlowHeaderPropsAreEqual);
export const MenuScreenHeader = MenuFlowHeader;
export { MenuFlowHeader };
export default MenuFlowHeader;
